import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import SEO from '../components/SEO';
import { formatPostDate, getAllPosts } from './posts';
import { legacyPosts } from './legacyPosts';
import { topics } from '../resources/topics';

interface BlogPost {
  slug: string;
  title: string;
  date: string;
  isoDate: string;
  updated?: string;
  excerpt: string;
  category?: string;
  cover?: string;
}

const allPosts: BlogPost[] = [
  ...legacyPosts.map((post) => ({
    slug: post.slug,
    title: post.title,
    date: post.date,
    isoDate: post.isoDate,
    excerpt: post.excerpt,
    category: post.category,
    cover: post.cover,
  })),
  ...getAllPosts().map((post) => ({
    slug: post.slug,
    title: post.title,
    date: formatPostDate(post.date),
    isoDate: post.date,
    updated: post.updated,
    excerpt: post.description,
    category: post.category,
    cover: post.cover,
  })),
].sort((a, b) => b.isoDate.localeCompare(a.isoDate));

export default function BlogIndex() {
  const [featured, ...rest] = allPosts;
  const latest = rest.slice(0, 3);
  const library = rest.slice(3);

  return (
    <BlogLayout wide>
      <SEO
        title="Sigma Migration & Ecommerce Reporting Guides"
        description="Worked Sigma formula examples, Power BI and Tableau migration guides, and practical fixes for Shopify, Amazon, and ecommerce reporting."
        url="https://www.greyfielddata.com/blog"
      />
      <header className="overflow-hidden rounded-[2rem] bg-[radial-gradient(circle_at_top_right,_#18356d_0,_#0a192f_42%,_#071525_100%)] px-6 py-12 text-white sm:px-10 sm:py-16 lg:px-14">
        <div className="max-w-3xl">
          <h1 className="text-4xl font-semibold leading-[1.1] tracking-tight sm:text-6xl">Data and analytics field notes</h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">Practical notes on data engineering and analytics across Snowflake, Sigma, Power BI, Tableau, and ecommerce data systems.</p>
        </div>
      </header>

      <section aria-label="Start with your reporting problem" className="grid gap-6 py-10 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-2xl font-semibold text-slate-950">Build or migrate in Sigma</h2>
          <p className="mt-3 text-slate-600">Translate the formulas, check the results, and plan the move.</p>
          <ul className="mt-5 space-y-3 text-[#1a4e94]">
            <li><Link className="underline underline-offset-4" to="/blog/excel-to-sigma-functions-cheat-sheet">Excel to Sigma formula examples</Link></li>
            <li><Link className="underline underline-offset-4" to="/blog/tableau-to-sigma-functions-cheat-sheet">Tableau calculations in Sigma</Link></li>
            <li><Link className="underline underline-offset-4" to="/blog/power-bi-dax-to-sigma-formulas-cheat-sheet">Power BI DAX patterns in Sigma</Link></li>
            <li><Link className="underline underline-offset-4" to="/blog/what-should-a-sigma-migration-assessment-include">Plan a migration assessment</Link></li>
            <li><Link className="underline underline-offset-4" to="/compare">Compare Sigma with Tableau, Power BI, Looker, and others</Link></li>
          </ul>
          <Link className="mt-6 inline-block font-semibold text-[#1a4e94]" to="/services/sigma-consulting">Get help with Sigma →</Link>
        </div>
        <div className="rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-2xl font-semibold text-slate-950">Make ecommerce numbers agree</h2>
          <p className="mt-3 text-slate-600">Work through channel overlap, refund timing, and missing fees.</p>
          <ul className="mt-5 space-y-3 text-[#1a4e94]">
            <li><Link className="underline underline-offset-4" to="/blog/avoid-double-counting-shopify-amazon-tiktok-orders">Avoid counting the same order twice</Link></li>
            <li><Link className="underline underline-offset-4" to="/blog/shopify-refund-date-vs-order-date">Reconcile Shopify refund and order dates</Link></li>
            <li><Link className="underline underline-offset-4" to="/blog/why-amazon-profit-changes-after-sale">Explain late Amazon fees and changing profit</Link></li>
            <li><Link className="underline underline-offset-4" to="/blog/daily-ecommerce-kpis-by-channel">Build a daily scorecard by channel</Link></li>
            <li><Link className="underline underline-offset-4" to="/compare">Compare Triple Whale, Polar, and others with a custom warehouse</Link></li>
          </ul>
          <Link className="mt-6 inline-block font-semibold text-[#1a4e94]" to="/ecommerce">Get help with ecommerce reporting →</Link>
        </div>
      </section>

      <nav aria-label="Browse guides by topic" className="flex flex-col gap-4 rounded-2xl bg-slate-50 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-semibold text-slate-900">Prefer a reading path?</p>
        <ul className="flex flex-wrap gap-2">
          {topics.map((topic) => (
            <li key={topic.slug}><Link to={`/resources/${topic.slug}`} className="inline-flex rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-[#1a4e94] hover:border-[#1a4e94]">{topic.title}</Link></li>
          ))}
          <li><Link to="/resources" className="inline-flex rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-[#1a4e94] hover:border-[#1a4e94]">Free tools</Link></li>
        </ul>
      </nav>

      {featured && (
        <section className="py-12 sm:py-16">
          <h2 className="mb-6 text-3xl font-semibold tracking-tight text-slate-950">Latest article</h2>
          <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_20px_60px_-35px_rgba(15,23,42,0.45)]">
            <Link to={`/blog/${featured.slug}`} className="group grid lg:grid-cols-[1.1fr_0.9fr]">
              <div className="flex aspect-video min-h-0 items-center justify-center overflow-hidden bg-[#071525] lg:aspect-auto lg:min-h-[390px]">
                {featured.cover ? (
                  <img src={featured.cover} alt="" className="h-full w-full object-contain transition duration-500 group-hover:scale-[1.01]" />
                ) : (
                  <div className="h-full bg-[linear-gradient(135deg,#071525,#163a75)]" />
                )}
              </div>
              <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-12">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-[#1a4e94]">{featured.category || 'Guide'}</span>
                  <time dateTime={featured.isoDate} className="text-slate-500">{featured.date}</time>
                  {featured.updated && featured.updated >= featured.isoDate && <span className="text-slate-500">Updated {formatPostDate(featured.updated)}</span>}
                </div>
                <h2 className="mt-5 text-3xl font-semibold leading-tight tracking-tight text-slate-950 transition-colors group-hover:text-[#1a4e94] sm:text-4xl">{featured.title}</h2>
                <p className="mt-5 text-base leading-7 text-slate-600">{featured.excerpt}</p>
                <span className="mt-7 inline-flex items-center gap-2 font-semibold text-[#1a4e94]">Read article <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span></span>
              </div>
            </Link>
          </article>
        </section>
      )}

      <section className="border-t border-slate-200 py-12 sm:py-16">
        <div className="mb-7">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Latest guides</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {latest.map((post) => (
            <article key={post.slug} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
              <Link to={`/blog/${post.slug}`} className="group flex h-full flex-col">
                <div className="aspect-[1200/630] overflow-hidden bg-[#071525]">
                  {post.cover ? <img src={post.cover} alt="" className="h-full w-full object-contain transition duration-500 group-hover:scale-[1.01]" /> : <div className="h-full bg-[linear-gradient(135deg,#071525,#163a75)]" />}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
                    <span className="font-semibold uppercase tracking-wide text-[#1a4e94]">{post.category || 'Guide'}</span>
                    <time dateTime={post.isoDate} className="text-slate-500">{post.date}</time>
                    {post.updated && post.updated >= post.isoDate && <span className="text-slate-500">Updated {formatPostDate(post.updated)}</span>}
                  </div>
                  <h3 className="mt-4 text-xl font-semibold leading-snug text-slate-950 transition-colors group-hover:text-[#1a4e94]">{post.title}</h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{post.excerpt}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-[#1a4e94]">Read article <span aria-hidden="true">→</span></span>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 py-12 sm:py-16">
        <h2 className="mb-7 text-3xl font-semibold tracking-tight text-slate-950">Guides and comparisons</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {library.map((post, index) => (
            <article key={post.slug} className="rounded-2xl border border-slate-200 bg-slate-50 transition hover:border-blue-200 hover:bg-white hover:shadow-lg">
              <Link to={`/blog/${post.slug}`} className="group flex h-full gap-5 p-6">
                {post.cover ? (
                  <img src={post.cover} alt="" width="1200" height="630" loading="lazy" decoding="async" className="hidden aspect-[1200/630] w-36 shrink-0 self-start rounded-lg bg-[#071525] object-cover sm:block" />
                ) : (
                  <span className="pt-1 text-sm font-semibold tabular-nums text-slate-400">{String(index + 1).padStart(2, '0')}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className="font-semibold uppercase tracking-wide text-[#1a4e94]">{post.category || 'Guide'}</span>
                    <time dateTime={post.isoDate} className="text-slate-500">{post.date}</time>
                    {post.updated && post.updated >= post.isoDate && <span className="text-slate-500">Updated {formatPostDate(post.updated)}</span>}
                  </div>
                  <h3 className="mt-3 text-lg font-semibold leading-snug text-slate-950 transition-colors group-hover:text-[#1a4e94]">{post.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600">{post.excerpt}</p>
                </div>
                <span aria-hidden="true" className="self-center text-xl text-[#1a4e94] transition-transform group-hover:translate-x-1">→</span>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="rounded-3xl bg-slate-950 px-6 py-10 text-white sm:px-10 sm:py-12">
        <div className="flex flex-col justify-between gap-7 lg:flex-row lg:items-center">
          <div>
            <h2 className="max-w-2xl text-3xl font-semibold tracking-tight">Need help with Snowflake, Sigma, or a reporting problem?</h2>
          </div>
          <a href="https://cal.com/linustse/meeting-with-linus?overlayCalendar=true" target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center justify-center rounded-xl bg-white px-6 py-3 font-semibold text-slate-950 transition hover:bg-sky-100">Get help with your data stack <span aria-hidden="true" className="ml-2">→</span></a>
        </div>
      </section>
    </BlogLayout>
  );
}
